import React from "react";
import "./style.css";
import CorrectionBox from "./CorrectionBox";
import { Card, CardBody } from "reactstrap";
import { ReactComponent as TickIcon } from "assets/img/svg/TickIcon.svg";
import { ReactComponent as CrossIcon } from "assets/img/svg/fi_x.svg";
import { ReactComponent as CloseFormIcon } from "assets/img/svg/CrossIcon.svg";
import IconButtonWrapper from "views/pages/manage/common/form-fields-mui/IconButtonWrapper";

const statusLineColors = {
  completed: "bg-black",
  initiated: "bg-black",
  reinitiated: "bg-black",
  expired: "bg-black",
  active: "bg-yellow-500",
  in_process: "bg-yellow-500",
  action_needed: "bg-yellow-500",
  user_recheck: "bg-yellow-500",
  rejected: "bg-red-500",
  failed: "bg-red-500",
  upcoming: "bg-gray-300",
  success: "bg-black",
  approved: "bg-black",
  "Payment Rejection - Full": "bg-red-500",
  "Payment Rejection - Partial": "bg-red-500",
};

// Grouped categories
const success = [
  "completed",
  "initiated",
  "reinitiated",
  "expired",
  "success",
  "approved",
];
const pending = ["active", "in_process"];
const actionNeeded = ["action_needed", "user_recheck"];
const failed = [
  "rejected",
  "failed",
  "Payment Rejection - Full",
  "Payment Rejection - Partial",
];
const upcoming = ["upcoming"];

const showSubsteps = (sub = []) => {
  return sub.some((item) => item?.status !== "upcoming");
};

const isIn = (status, list) => list.includes(status);

const StatusIcon = ({ status, index, isSub, parentStatus, item }) => {
  const size = isSub ? 10 : 16;

  let finalStatus = status;
  if (
    isSub &&
    !success.includes(parentStatus) &&
    status !== "upcoming" &&
    !failed.includes(status)
  ) {
    finalStatus = "in_process";
  }

  // SUCCESS STATES
  if (isIn(finalStatus, success)) {
    return {
      finalStatus,
      icon: isSub ? (
        <div className="dot dot-green" style={{ width: size, height: size }} />
      ) : (
        <TickIcon
          style={{
            backgroundColor: "green",
            borderRadius: "50%",
            padding: "4px",
          }}
        />
      ),
    };
  }

  // PENDING STATES
  if (isIn(finalStatus, pending)) {
    return {
      finalStatus,
      icon: isSub ? (
        <div className="dot dot-yellow" style={{ width: size, height: size }} />
      ) : (
        <div className="step-badge">{index + 1}</div>
      ),
    };
  }

  // ACTION NEEDED STATES
  if (isIn(finalStatus, actionNeeded)) {
    return {
      finalStatus,
      icon:
        status !== "in_process" || !isSub ? (
          <div
            className="dot dot-yellow dot-stepNumber"
            style={{ width: size, height: size }}
          >
            {!isSub && index + 1}
          </div>
        ) : (
          <div className="dot circle">
            <div className="circle-inner"></div>
          </div>
        ),
    };
  }

  //  FAILED STATES
  if (isIn(finalStatus, failed)) {
    return {
      finalStatus,
      icon: isSub ? (
        <div className="dot dot-red" style={{ width: size, height: size }} />
      ) : (
        <CrossIcon
          style={{
            backgroundColor: "#DC2804",
            borderRadius: "50%",
            padding: "2px",
          }}
        />
      ),
    };
  }

  // UPCOMING (Default)
  return {
    finalStatus,
    icon: (
      <div
        className="dot dot-gray dot-stepNumber"
        style={{ width: size, height: size }}
      >
        {!isSub && index + 1}
      </div>
    ),
  };
};

//  Timeline Item
const TimelineItem = ({
  item,
  index,
  isSub = false,
  parentStatus,
  isLast = false,
}) => {
  const { finalStatus, icon } = StatusIcon({
    status: item.status,
    index,
    isSub,
    parentStatus,
    item,
  });

  return (
    <div className="timeline-item">
      <div className="timeline-row">
        <div className="timeline-icon-wrapper">
          <div className="timeline-icon">{icon}</div>
          {!isLast && (
            <span
              className={`timeline-vertical-line ${statusLineColors[finalStatus]}`}
            />
          )}
        </div>

        <div className="timeline-content color-grey-80">
          <div className="semi-bolder">{item.title}</div>

          {finalStatus === "in_process" &&
            item.status === "in_process" &&
            (!item?.subSteps || isSub) && (
              <div className="timeline-status-in-process">In process</div>
            )}

          {isIn(finalStatus, failed) && (
            <div className="timeline-status-rejected">
              {finalStatus.charAt(0).toUpperCase() + finalStatus.slice(1)}
            </div>
          )}

          {isIn(finalStatus, failed) && item.remark && (
            <div className="regular-small color-grey-80">{item.remark}</div>
          )}
          {isIn(finalStatus, pending) &&
            item.remark &&
            item?.isRemarkNedded && (
              <div className="regular-small color-grey-80">{item.remark}</div>
            )}
          {isIn(finalStatus, success) &&
            item.remark &&
            item?.isRemarkNedded && (
              <div className="regular-small color-grey-80">{item.remark}</div>
            )}
          {item.status !== "in_process" && item.updatedOn && (
            <div className="regular-small color-grey-80">
              {new Date(item.updatedOn).toLocaleString("en-US", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
              {item.updatedBy && ` by ${item.updatedBy}`}
            </div>
          )}

          {isIn(finalStatus, actionNeeded) && item.remark && (
            <CorrectionBox
              message={item.remark}
              onEdit={item.onEdit}
              actionNeededText={item?.actionNeededText || ""}
            />
          )}
        </div>
      </div>
    </div>
  );
};

//  Main Component
const TransferTimeline = ({
  steps,
  heading = "Transfer status",
  marginLeft = "24px",
  marginBottom = null,
  closeForm,
}) => {
  return (
    <Card style={{ marginLeft: marginLeft, marginBottom: marginBottom }}>
      <CardBody>
        <div>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="large-text label-color mb-0">{heading}</div>
            {closeForm && (
              <IconButtonWrapper onClick={closeForm}>
                <CloseFormIcon />
              </IconButtonWrapper>
            )}
          </div>
          <div className="timeline-wrapper">
            <div className="timeline-list">
              {steps.map((step, index) => {
                const hasVisibleSubSteps =
                  step.subSteps && showSubsteps(step.subSteps);

                const isLastParent =
                  index === steps.length - 1 && !hasVisibleSubSteps;

                return (
                  <React.Fragment key={index}>
                    <TimelineItem
                      item={step}
                      index={index}
                      isLast={isLastParent}
                    />

                    {hasVisibleSubSteps &&
                      step.subSteps.map((sub, subIndex) => {
                        const isLastSub =
                          index === steps.length - 1 &&
                          subIndex === step.subSteps.length - 1;

                        return (
                          <TimelineItem
                            key={`${index}-sub-${subIndex}`}
                            item={sub}
                            index={subIndex}
                            isSub
                            parentStatus={step.status}
                            isLast={isLastSub}
                          />
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default TransferTimeline;
